import { BCurve } from '@ue-too/curve';
import { PointCal } from '@ue-too/math';

import type { AnchorId, BezierAnchor, BezierCurveGuide, HandleType } from './guide-types';
import type { TbcPoint } from './tbc-types';

/** Half-length used to place the default 2-anchor curve (matches the line guide). */
export const DEFAULT_BEZIER_HALF_LENGTH = 80;
/** Vertical handle offset for the default S-curve. */
const DEFAULT_BEZIER_HANDLE_OFFSET = 40;
/** Sampling resolution per segment for rendering / hover distance. */
const SEGMENT_SAMPLE_STEPS = 24;

/** Build one cubic `BCurve` per segment. Open: anchors.length-1 segments.
 *  Closed: an extra wrap segment from the last anchor back to the first. */
export function buildSegments(guide: BezierCurveGuide): BCurve[] {
    const a = guide.anchors;
    if (a.length < 2) return [];
    const segs: BCurve[] = [];
    const last = guide.closed ? a.length : a.length - 1;
    for (let i = 0; i < last; i++) {
        const cur = a[i];
        const next = a[(i + 1) % a.length];
        segs.push(new BCurve([
            { x: cur.point.x, y: cur.point.y },
            { x: cur.rightHandle.x, y: cur.rightHandle.y },
            { x: next.leftHandle.x, y: next.leftHandle.y },
            { x: next.point.x, y: next.point.y },
        ]));
    }
    return segs;
}

/** Built segments cached per guide object, validated by an anchor signature.
 *  Building a `BCurve` runs Gauss-quadrature arc-length setup per segment,
 *  and the renderer asks for a tangent (or point) per bezier-snapped stitch on
 *  every full rebuild — every zoom step — so rebuilding per query made a
 *  54-stitch diagram with 65 bezier snaps spend ~47 ms per zoom event. The
 *  engine replaces guides immutably, but the signature check means an in-place
 *  edit can never serve stale segments either. Nothing mutates a built
 *  `BCurve` (an evaluator holds them by reference on purpose), so the
 *  cached list is safe to hand to every query. */
const segmentCache = new WeakMap<BezierCurveGuide, { sig: string; segs: BCurve[] }>();

function anchorSignature(guide: BezierCurveGuide): string {
    let sig = guide.closed ? 'c' : 'o';
    for (const a of guide.anchors) {
        sig += `|${a.point.x},${a.point.y},${a.leftHandle.x},${a.leftHandle.y},${a.rightHandle.x},${a.rightHandle.y}`;
    }
    return sig;
}

/** The guide's cubic segments, built once per guide shape (see the cache
 *  note above). Same result as {@link buildSegments}; prefer this for any
 *  repeated query. */
export function segmentsOf(guide: BezierCurveGuide): BCurve[] {
    const sig = anchorSignature(guide);
    const hit = segmentCache.get(guide);
    if (hit != null && hit.sig === sig) return hit.segs;
    const segs = buildSegments(guide);
    segmentCache.set(guide, { sig, segs });
    return segs;
}

/** Total arc length across all segments. */
export function totalLength(guide: BezierCurveGuide): number {
    return segmentsOf(guide).reduce((sum, s) => sum + s.fullLength, 0);
}

/** Sample the whole curve into a polyline (for rendering / hover hit-tests). */
export function samplePolyline(guide: BezierCurveGuide, stepsPerSegment = SEGMENT_SAMPLE_STEPS): TbcPoint[] {
    const segs = segmentsOf(guide);
    const pts: TbcPoint[] = [];
    for (let si = 0; si < segs.length; si++) {
        const lut = segs[si].getLUT(stepsPerSegment);
        for (let i = si === 0 ? 0 : 1; i < lut.length; i++) {
            pts.push({ x: lut[i].x, y: lut[i].y });
        }
    }
    return pts;
}

/** Shoelace signed area of a closed polygon (last point joins the first), in
 *  the points' own frame: positive when `(x, y)` winds from +x toward +y. With
 *  y down that is clockwise on screen. 0 for fewer than three points. */
export function signedArea(points: readonly TbcPoint[]): number {
    let twice = 0;
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        twice += a.x * b.y - b.x * a.y;
    }
    return twice / 2;
}

/** Map a global arc-length fraction s∈[0,1] to a world point. */
export function pointAtS(guide: BezierCurveGuide, s: number): TbcPoint {
    return pointAtSOnSegments(segmentsOf(guide), guide.anchors[0]?.point ?? { x: 0, y: 0 }, s);
}

/** Resolve a global arc-length fraction s∈[0,1] to the segment it lands on
 *  plus the local arc-length percentage within that segment (clamped to
 *  [0,1]). Null for a degenerate curve (no segments / zero total length).
 *  Single source of the mapping shared by point and tangent evaluation so
 *  both always agree on WHERE s is. */
function locateS(segs: BCurve[], s: number): { seg: BCurve; localPct: number } | null {
    if (segs.length === 0) return null;
    const total = segs.reduce((sum, seg) => sum + seg.fullLength, 0);
    if (total <= 0) return null;
    const target = Math.max(0, Math.min(1, s)) * total;
    let acc = 0;
    for (const seg of segs) {
        const len = seg.fullLength;
        if (target <= acc + len || seg === segs[segs.length - 1]) {
            const localPct = len <= 0 ? 0 : (target - acc) / len;
            return { seg, localPct: Math.max(0, Math.min(1, localPct)) };
        }
        acc += len;
    }
    return { seg: segs[segs.length - 1], localPct: 1 };
}

/** Shared body of {@link pointAtS} / {@link makeArcLengthEvaluator}: evaluate
 *  over an already-built segment list so callers that query many fractions can
 *  reuse the segments (and each BCurve's internal arc-length LUT). `fallback`
 *  is returned (copied) for a degenerate curve (no segments / zero length). */
function pointAtSOnSegments(segs: BCurve[], fallback: TbcPoint, s: number): TbcPoint {
    const loc = locateS(segs, s);
    if (loc == null) return { ...fallback };
    // getPointbyPercentage is LUT-based and never returns the exact
    // endpoint; use get() at the boundaries so a stitch at s=0/1 sits
    // precisely on the anchor.
    const p = loc.localPct <= 0 ? loc.seg.get(0)
        : loc.localPct >= 1 ? loc.seg.get(1)
        : loc.seg.getPointbyPercentage(loc.localPct);
    return { x: p.x, y: p.y };
}

/** Unit tangent (curve direction) at a global arc-length fraction s∈[0,1].
 *  Returns null for a degenerate curve (no length / zero derivative at a
 *  cusp). Used to orient per-leg symbol adornments — e.g. the single-crochet
 *  `+` crossing bar — along a bezier guide at the snap point. */
export function tangentAtS(guide: BezierCurveGuide, s: number): TbcPoint | null {
    return tangentAtSOnSegments(segmentsOf(guide), s);
}

/** Analytic unit tangent — normalized BCurve derivative at the segment/local
 *  percentage that {@link locateS} resolves s to, with exact derivative(0)/
 *  derivative(1) at the boundaries (mirroring pointAtSOnSegments' endpoint
 *  special case). The earlier finite-difference version broke at s=1: the
 *  LUT behind getPointbyPercentage snaps the last ~1e-3 of s to the exact
 *  endpoint, so both difference samples coincided → zero vector → null.
 *  Direction is parameterization-independent, so the normalized t-space
 *  derivative IS the arc-length tangent. */
function tangentAtSOnSegments(segs: BCurve[], s: number): TbcPoint | null {
    const loc = locateS(segs, s);
    if (loc == null) return null;
    const d = loc.localPct <= 0 ? loc.seg.derivative(0)
        : loc.localPct >= 1 ? loc.seg.derivative(1)
        : loc.seg.derivativeByPercentage(loc.localPct);
    const len = Math.hypot(d.x, d.y);
    if (len === 0) return null;
    return { x: d.x / len, y: d.y / len };
}

/** Build-once / query-many arc-length evaluator over the guide's (cached)
 *  segments — a closure that skips even the signature check per query.
 *  Results are identical to `pointAtS`/`tangentAtS` — same algorithm, shared
 *  body. */
export function makeArcLengthEvaluator(guide: BezierCurveGuide): {
    pointAt: (s: number) => TbcPoint;
    tangentAt: (s: number) => TbcPoint | null;
} {
    const segs = segmentsOf(guide);
    const fallback = guide.anchors[0]?.point ?? { x: 0, y: 0 };
    const pointAt = (s: number): TbcPoint => pointAtSOnSegments(segs, fallback, s);
    const tangentAt = (s: number): TbcPoint | null => tangentAtSOnSegments(segs, s);
    return { pointAt, tangentAt };
}

/** Samples a built segment list into a base-frame polyline (mirrors
 *  {@link samplePolyline} but over already-built segments so the LUTs aren't
 *  rebuilt). */
function samplePolylineFromSegs(segs: BCurve[], stepsPerSegment = SEGMENT_SAMPLE_STEPS): TbcPoint[] {
    const pts: TbcPoint[] = [];
    for (let si = 0; si < segs.length; si++) {
        const lut = segs[si].getLUT(stepsPerSegment);
        for (let i = si === 0 ? 0 : 1; i < lut.length; i++) pts.push({ x: lut[i].x, y: lut[i].y });
    }
    return pts;
}

/** A bezier evaluator: `segs`/`rawPolyline` are the expensive, shape-only
 *  assets (base frame), built once; `xf` is a rigid placement composed over
 *  every read. `buildBezierEvaluator` places it at the identity, and nothing
 *  moves one any more (stitch arrays carried theirs across a drag). */
export interface BezierEvaluator {
    pointAt(s: number): TbcPoint;
    tangentAt(s: number): TbcPoint | null;
    readonly segs: BCurve[];
    readonly fallback: TbcPoint;
    readonly rawPolyline: TbcPoint[];
    readonly xf: RigidTransform;
}

function makeBezierEvaluator(
    segs: BCurve[],
    fallback: TbcPoint,
    rawPolyline: TbcPoint[],
    xf: RigidTransform,
): BezierEvaluator {
    return {
        segs,
        fallback,
        rawPolyline,
        xf,
        pointAt: (s: number) => applyRigid(xf, pointAtSOnSegments(segs, fallback, s)),
        tangentAt: (s: number) => {
            const t = tangentAtSOnSegments(segs, s);
            return t == null ? null : applyRigidDirection(xf, t);
        },
    };
}

/** Build a fresh evaluator from a guide (segments + LUTs built once, identity
 *  placement). */
export function buildBezierEvaluator(guide: BezierCurveGuide): BezierEvaluator {
    const segs = buildSegments(guide);
    const fallback = guide.anchors[0]?.point ?? { x: 0, y: 0 };
    return makeBezierEvaluator(segs, fallback, samplePolylineFromSegs(segs), IDENTITY_RIGID);
}

/** Project a world point onto the curve. Returns the nearest point plus its
 *  global arc-length fraction s∈[0,1], or null if the curve has no segments. */
export function projectPointToCurve(guide: BezierCurveGuide, world: TbcPoint): { s: number; point: TbcPoint } | null {
    return makeCurveProjector(guide)(world);
}

/** Build-once / query-many form of {@link projectPointToCurve}: the guide's
 *  segments and their arc-length offsets are taken once. A query may name the
 *  segments to look at (indices in ascending order; an index the curve doesn't
 *  have is skipped); the result is then the nearest point on those segments,
 *  with `s` still global. Over every segment it IS `projectPointToCurve`, and
 *  over a subset that holds the curve's nearest point it is the same answer to
 *  the bit: one loop, the same offsets, ties to the earlier segment. So a
 *  caller that knows where the answer lies can project in O(window), not
 *  O(segments). Null when the curve has no segments or no length. */
export function makeCurveProjector(
    guide: BezierCurveGuide,
): (world: TbcPoint, segmentIndices?: readonly number[]) => { s: number; point: TbcPoint } | null {
    const segs = segmentsOf(guide);
    // starts[i] is the arc length before segment i, summed in segment order.
    const starts: number[] = [];
    let total = 0;
    for (const seg of segs) {
        starts.push(total);
        total += seg.fullLength;
    }
    return (world, segmentIndices) => {
        if (segs.length === 0 || total <= 0) return null;
        let best: { d: number; s: number; point: TbcPoint } | null = null;
        for (const i of segmentIndices ?? segs.keys()) {
            const seg = segs[i];
            if (seg == null) continue;
            const proj = seg.getProjection(world);
            const d = PointCal.distanceBetweenPoints(world, proj.projection);
            const s = (starts[i] + seg.lengthAtT(proj.tVal)) / total;
            if (best == null || d < best.d) {
                best = { d, s, point: { x: proj.projection.x, y: proj.projection.y } };
            }
        }
        return best == null ? null : { s: best.s, point: best.point };
    };
}

/** Even arc-length fractions for distributing n handles. Open curves use the
 *  interior (i+1)/(n+1) (no stitch sits exactly on an endpoint, matching the
 *  line guide); closed loops use i/n (evenly around, no endpoints). */
export function evenArcLengthFractions(guide: BezierCurveGuide, n: number): number[] {
    if (n <= 0) return [];
    if (guide.closed) return Array.from({ length: n }, (_, i) => i / n);
    return Array.from({ length: n }, (_, i) => (i + 1) / (n + 1));
}

/** Mirror a moved handle's partner when its partner is ALIGNED. `movedHandle` is
 *  the new position of the side being dragged; returns the new partner handle
 *  position preserving the partner's own length. Pure — caller writes it back. */
export function mirrorAlignedPartner(anchorPoint: TbcPoint, movedHandle: TbcPoint, partnerHandle: TbcPoint): TbcPoint {
    const dir = PointCal.subVector(movedHandle, anchorPoint);
    const mag = PointCal.magnitude(dir);
    const partnerLen = PointCal.distanceBetweenPoints(partnerHandle, anchorPoint);
    if (mag <= 0) return { ...partnerHandle };
    const unit = PointCal.divideVectorByScalar(dir, mag);
    return {
        x: anchorPoint.x - unit.x * partnerLen,
        y: anchorPoint.y - unit.y * partnerLen,
    } as TbcPoint;
}

/** The anchor fields `normalizeVectorHandles` reads. Guide curves and bezier
 *  annotations share the HandleType rules, so both pass through it. */
type VectorNormalizable = Pick<BezierAnchor, 'point' | 'leftHandle' | 'rightHandle' | 'leftType' | 'rightType'>;

/** Recompute VECTOR-typed handles from neighbor anchors (1/3 toward neighbor).
 *  Endpoints with no neighbor on a side keep their existing handle. Returns a
 *  new anchors array (does not mutate). */
export function normalizeVectorHandles<A extends VectorNormalizable>(anchors: readonly A[], closed: boolean): A[] {
    const n = anchors.length;
    return anchors.map((a, i) => {
        const prev = closed ? anchors[(i - 1 + n) % n] : anchors[i - 1];
        const next = closed ? anchors[(i + 1) % n] : anchors[i + 1];
        let { leftHandle, rightHandle } = a;
        if (a.leftType === 'vector' && prev != null) {
            leftHandle = {
                x: a.point.x + (prev.point.x - a.point.x) / 3,
                y: a.point.y + (prev.point.y - a.point.y) / 3,
            };
        }
        if (a.rightType === 'vector' && next != null) {
            rightHandle = {
                x: a.point.x + (next.point.x - a.point.x) / 3,
                y: a.point.y + (next.point.y - a.point.y) / 3,
            };
        }
        return { ...a, leftHandle, rightHandle };
    });
}

/** How far past an end `extendedEndAnchor` places a new anchor (a bezier
 *  guide's `addAnchor`). */
const ADD_ANCHOR_STEP = 60;

/** The anchor added past one end of an open curve: one fixed step beyond the
 *  end anchor along its incoming direction (+x when there is no neighbour),
 *  both handles ALIGNED on that line a third of the step away. Segments run
 *  prev.rightHandle → next.leftHandle, so the handle facing back toward the
 *  curve is the LEFT one when appending but the RIGHT one when prepending.
 *  Giving a prepended anchor the append layout starts the curve heading away
 *  from the rest of it, a visible hook. */
export function extendedEndAnchor(anchors: readonly BezierAnchor[], where: 'append' | 'prepend', id: BezierAnchor['id']): BezierAnchor {
    const end = where === 'append' ? anchors[anchors.length - 1] : anchors[0];
    const ref = where === 'append' ? anchors[anchors.length - 2] : anchors[1];
    let dx = ADD_ANCHOR_STEP, dy = 0;
    if (ref != null) {
        const ux = end.point.x - ref.point.x, uy = end.point.y - ref.point.y;
        const m = Math.hypot(ux, uy) || 1;
        dx = (ux / m) * ADD_ANCHOR_STEP;
        dy = (uy / m) * ADD_ANCHOR_STEP;
    }
    const point = { x: end.point.x + dx, y: end.point.y + dy };
    const inward = { x: point.x - dx / 3, y: point.y - dy / 3 };
    const outward = { x: point.x + dx / 3, y: point.y + dy / 3 };
    return {
        id,
        point,
        leftHandle: where === 'append' ? inward : outward,
        rightHandle: where === 'append' ? outward : inward,
        leftType: 'aligned',
        rightType: 'aligned',
    };
}

/** The default 2-anchor open S-curve centered on `world`. */
export function defaultBezierAnchors(world: TbcPoint): BezierAnchor[] {
    const h = DEFAULT_BEZIER_HALF_LENGTH;
    const o = DEFAULT_BEZIER_HANDLE_OFFSET;
    const mkType = (): HandleType => 'aligned';
    return [
        {
            id: crypto.randomUUID(),
            point: { x: world.x - h, y: world.y },
            rightHandle: { x: world.x - h + o, y: world.y - o },
            leftHandle: { x: world.x - h - o, y: world.y + o },
            leftType: mkType(), rightType: mkType(),
        },
        {
            id: crypto.randomUUID(),
            point: { x: world.x + h, y: world.y },
            leftHandle: { x: world.x + h - o, y: world.y + o },
            rightHandle: { x: world.x + h + o, y: world.y - o },
            leftType: mkType(), rightType: mkType(),
        },
    ];
}

/** A smooth curve through `points` (Catmull-Rom): one anchor per point, fresh
 *  ids, both handle types `'aligned'`. Each anchor's handles sit on the line
 *  through it parallel to its neighbours' chord, `d = (next − prev) / 6` to
 *  either side, so every segment bends instead of cutting straight across.
 *  Closed curves wrap. An open curve's end has one neighbour: its handles go a
 *  third of the end segment to either side of the point. With fewer than three
 *  points a closed curve degenerates (callers refuse it).
 *
 *  `anchorId` names anchor `k`; without it each anchor gets a fresh random id.
 *  A caller that must build the same curve on every replica (the SP-125
 *  legacy-array converter) passes one. */
export function smoothAnchorsThrough(
    points: readonly TbcPoint[],
    closed: boolean,
    anchorId: (k: number) => AnchorId = () => crypto.randomUUID(),
): BezierAnchor[] {
    const n = points.length;
    return points.map((p, i) => {
        let dx = 0;
        let dy = 0;
        if (closed || (i > 0 && i < n - 1)) {
            const prev = points[(i - 1 + n) % n];
            const next = points[(i + 1) % n];
            dx = (next.x - prev.x) / 6;
            dy = (next.y - prev.y) / 6;
        } else if (n > 1 && i === 0) {
            dx = (points[1].x - p.x) / 3;
            dy = (points[1].y - p.y) / 3;
        } else if (n > 1) {
            dx = (p.x - points[n - 2].x) / 3;
            dy = (p.y - points[n - 2].y) / 3;
        }
        return {
            id: anchorId(i),
            point: { x: p.x, y: p.y },
            leftHandle: { x: p.x - dx, y: p.y - dy },
            rightHandle: { x: p.x + dx, y: p.y + dy },
            leftType: 'aligned',
            rightType: 'aligned',
        };
    });
}

/** A rigid motion (rotation + translation, no scale): p' = R·p + t.
 *  Stored as the rotation's cos/sin plus the translation (tx,ty). A bezier
 *  evaluator's placement (`BezierEvaluator.xf`). */
export type RigidTransform = { cos: number; sin: number; tx: number; ty: number };

export const IDENTITY_RIGID: RigidTransform = Object.freeze({ cos: 1, sin: 0, tx: 0, ty: 0 });

/** Apply a rigid motion to a point (rotate then translate). */
export function applyRigid(xf: RigidTransform, p: TbcPoint): TbcPoint {
    return {
        x: xf.cos * p.x - xf.sin * p.y + xf.tx,
        y: xf.sin * p.x + xf.cos * p.y + xf.ty,
    };
}

/** Apply only the rotation part to a direction/tangent vector (no translation).
 *  Rotation preserves length, so a unit tangent stays unit. */
export function applyRigidDirection(xf: RigidTransform, v: TbcPoint): TbcPoint {
    return { x: xf.cos * v.x - xf.sin * v.y, y: xf.sin * v.x + xf.cos * v.y };
}
